import { ArrowLeftIcon, Button, Flex } from '@fluentui/react-northstar';
import { cloneDeep } from 'lodash';
import React from 'react';
import { EditTicketForm } from '../../shared/components/EditTicketForm';
import { EditTicketFormSkeleton } from '../../shared/components/skeletonloaders/EditTicketFormSkeleton';
import { ACTIONS, EditTicketState } from './editTicketState';

interface TicketEditPanelProps {
  state: EditTicketState;
  globalTheme: any;
  isUserLicensed: boolean;
  tikitConfig: TikitConfiguration;
  isItemSelected: boolean;
  edittingFormClass: string;
  dispatch: React.Dispatch<any>;
  setSideViewTicketsState: (updater: any) => void;
  SetDetailCardApprovalsList: (list: TicketApprovals[]) => void;
  SetDetailCardTaskList: (list: TicketTask[]) => void;
  saveComplete: (action: any) => Promise<void> | void;
  onToolbarInteraction: (action: any) => Promise<void> | void;
  /**
   * Desktop only: the panel narrows to an empty rail. The form stays mounted underneath, so
   * unsaved property edits survive collapsing and the leave warning still sees them.
   */
  collapsed?: boolean;
}

/**
 * Right-hand edit form panel. Renders a skeleton when another ticket is being
 * loaded (`isItemSelected`), otherwise the full `EditTicketForm` with a mobile
 * back button.
 */
const TicketEditPanelImpl: React.FC<TicketEditPanelProps> = ({
  state,
  globalTheme,
  isUserLicensed,
  tikitConfig,
  isItemSelected,
  edittingFormClass,
  dispatch,
  setSideViewTicketsState,
  SetDetailCardApprovalsList,
  SetDetailCardTaskList,
  saveComplete,
  onToolbarInteraction,
  collapsed = false
}) => {
  if (isItemSelected) {
    return (
      <Flex.Item
        className={`hidden md:block`}
        styles={{
          width: collapsed ? '20px' : '25%',
          backgroundColor: globalTheme.siteVariables.colorScheme.default.background
        }}
      >
        {/* Always one element, collapsed or not: Flex.Item puts its className and styles on its
            only child and throws (React.Children.only) when there is none, unmounting the page. */}
        <div className={collapsed ? '' : 'p-5 m-0 overflow-auto'}>
          {!collapsed && <EditTicketFormSkeleton />}
        </div>
      </Flex.Item>
    );
  }

  const isDropzoneDisabled =
    state?.fileTriggerChannel?.TriageChannel == null ||
    state?.fileTriggerChannel?.TriageChannel?.length < 1 ||
    state?.ticket == null ||
    state?.ticket?.Closed;

  return (
    <Flex.Item
      className={`collapsible-component ticket-properties-panel ${edittingFormClass} w-1/4 ${collapsed ? 'md:w-5 md:overflow-hidden' : ''} height--50 overflow-auto pb-10 print-hidden`}
      styles={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background }}
    >
      <div className={`p-5 m-0 overflow-auto mobile-editform ${collapsed ? 'md:hidden' : ''}`}>
        <div className={`block md:hidden mb-3`}>
          <Button
            onClick={() => dispatch({ type: ACTIONS.DONE_EDITTING })}
            className={`mb-1`}
            content={<ArrowLeftIcon size="large" />}
            text
            iconOnly
            style={{ color: globalTheme.siteVariables.colorScheme.brand.foregroundActive }}
          />
        </div>
        <EditTicketForm
          globalTheme={globalTheme}
          ticket={state.ticket}
          isUserLicensed={isUserLicensed}
          dispatchParentEvt={dispatch}
          unassigningState={state.isUnassigning}
          takingState={state.isTakingTicket}
          applyTemplateState={state.isApplyTemplateOpen}
          applyLifecycleState={state.isApplyLifecycleOpen}
          savingState={state.isSavingState}
          signedInUser={state.signedInUser}
          onInteraction={saveComplete}
          onToolbarInteraction={onToolbarInteraction}
          isDropzoneDisabled={isDropzoneDisabled}
          fileTriggerChannel={state?.fileTriggerChannel?.TriageChannel}
          setTicketsStateHandler={setSideViewTicketsState}
          SetdetailCardApprovalsList={SetDetailCardApprovalsList}
          setDetailCardTaskList={SetDetailCardTaskList}
          resetUpdateTicketState={state.resetUpdateTicketState}
          TikitConfig={tikitConfig}
          defaultTicket={cloneDeep(state.defaultTicket)}
          isSendTemplateFormOpen={state.isSendTemplateFormOpen}
          reloadAttachments={state.reloadAttachments}
        />
      </div>
    </Flex.Item>
  );
};

export const TicketEditPanel = React.memo(TicketEditPanelImpl);
